(() => {
  const root = document.getElementById("portrait-wizard");
  if (!root) return;
  const cfg = JSON.parse(document.getElementById("portrait-wizard-config").textContent);
  const screen = root.querySelector("[data-pw-screen]");

  // session id (stable per browser) for the backend quota
  let sessionId = null;
  try {
    sessionId = localStorage.getItem("pw_session");
    if (!sessionId) { sessionId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random()); localStorage.setItem("pw_session", sessionId); }
  } catch (_) { sessionId = String(Date.now() + Math.random()); }

  // --- Resume: persist the last generation so the customer can come back ----
  // Snapshot of the last successful generation; mirrors the backend's 30-day
  // purge window (an older snapshot would 404 anyway).
  const SNAP_KEY = "hn-portrait:v1";
  const SNAP_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  // Whether the design currently in S belongs to this visitor (their own
  // generation, or a same-device resume). A foreign shared ?design link is
  // view-only: saveSnapshot() must never persist it over the visitor's slot.
  let ownsDesign = false;
  function readSnapshot() {
    try {
      const raw = localStorage.getItem(SNAP_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw);
      if (!snap || !snap.designId || Date.now() - (snap.savedAt || 0) > SNAP_MAX_AGE_MS) {
        localStorage.removeItem(SNAP_KEY);
        return null;
      }
      return snap;
    } catch (_) {
      clearSnapshot(); // a corrupted snapshot would fail every future read
      return null;
    }
  }
  function saveSnapshot() {
    if (!S.designId || !ownsDesign) return;
    try {
      localStorage.setItem(SNAP_KEY, JSON.stringify({
        designId: S.designId, themeKey: S.genThemeKey, themeLabel: S.themeLabel,
        uploadId: S.genUploadId, email: S.email, remaining: S.remaining,
        format: S.format, finition: S.finition, savedAt: Date.now(),
      }));
    } catch (_) {}
  }
  function clearSnapshot() {
    try { localStorage.removeItem(SNAP_KEY); } catch (_) {}
  }
  // Keeps the address bar shareable/bookmarkable (?design=<uuid>) without reloads.
  function setDesignParam(id) {
    try {
      const url = new URL(window.location.href);
      if (id) url.searchParams.set("design", id);
      else url.searchParams.delete("design");
      history.replaceState(null, "", url);
    } catch (_) {}
  }

  const S = {
    step: 0, themeKey: null, themeLabel: null,
    uploadId: null, uploadedFile: null,
    designId: null, previewUrl: null, remaining: cfg.maxRegen,
    genThemeKey: null, genUploadId: null, // theme + photo the saved generation was made from
    file: null, email: "", consent: false, format: null, finition: null,
    view: "room", // "room" = in-situ salon, "art" = the framed painting alone
  };

  const formats = [...new Set(cfg.variants.map((v) => v.format))];
  const finitions = [...new Set(cfg.variants.map((v) => v.finition))];

  // Frame finishes -> CSS background for the live framed mockup. Keys match the
  // product's "Finition" option labels; a missing key means "Sans cadre".
  const FRAME_BG = {
    "Noir": "linear-gradient(135deg,#2c2c2c,#0e0e0e 55%,#242424)",
    "Marron": "linear-gradient(135deg,#7c4f2c,#553318 55%,#6e4526)",
    "Gris foncé": "linear-gradient(135deg,#4d4d4d,#2c2c2c 55%,#424242)",
    "Gris clair": "linear-gradient(135deg,#dcdcd4,#c2c2ba 55%,#d6d6ce)",
    "Or antique": "linear-gradient(135deg,#f0d488,#b07d0a 45%,#e7c66f 70%,#946b10)",
    "Argent antique": "linear-gradient(135deg,#ededed,#a6a6a6 45%,#dcdcdc 70%,#9a9a9a)",
  };
  function ratioOf(format) {
    const m = String(format).match(/(\d+)\D+(\d+)/);
    return m ? `${m[1]} / ${m[2]}` : "4 / 5";
  }
  function widthCm(format) { const m = String(format).match(/(\d+)/); return m ? +m[1] : 40; }
  function themeThumb() { const t = cfg.themes.find((x) => x.key === S.themeKey); return t ? t.thumb : ""; }

  // A saved generation is "fresh" when it matches the current theme + photo.
  // Changing either makes it stale, which forces an explicit re-generation.
  function genFresh() { return !!S.designId && S.genThemeKey === S.themeKey && S.genUploadId === S.uploadId; }

  function mockup() {
    // In "art" view the size is irrelevant, so keep a fixed ratio (nothing
    // reflows when the size changes); in "room" view the real size ratio drives
    // the scale on the wall.
    const r = S.view === "art" ? "4 / 5" : ratioOf(S.format);
    const bg = FRAME_BG[S.finition];
    const img = `<img class="pw-mock-img" style="aspect-ratio:${r}" src="${S.previewUrl}" alt="">`;
    const frame = bg
      ? `<div class="pw-frame" style="background:${bg}"><span class="pw-mock-wrap">${img}<span class="pw-sheen"></span></span></div>`
      : `<div class="pw-frame pw-frame--none">${img}</div>`;
    if (cfg.roomImage && S.view === "room") {
      const w = (cfg.roomSize || 22) * (widthCm(S.format) / 50);
      return `<div class="pw-room" style="background-image:url(${cfg.roomImage})">
        <div class="pw-room-art" style="left:${cfg.roomX}%;top:${cfg.roomY}%;width:${w}%">${frame}</div></div>`;
    }
    return `<div class="pw-mock">${frame}</div>`;
  }

  // Layout: visual | controls. Side-by-side on desktop, stacked on mobile.
  function twoCol(visual, controls) {
    return `<div class="pw-body"><div class="pw-visual">${visual}</div><div class="pw-controls">${controls}</div></div>`;
  }

  function pickVariant(variants, format, finition) {
    return variants.find((v) => v.format === format && v.finition === finition) || null;
  }
  window.__pwTest = { pickVariant };

  function money(cents) {
    return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: cfg.currency });
  }

  // --- Clickable progress stepper -------------------------------------------
  // A step is reachable once its prerequisites are met. The result step is only
  // reachable with a fresh generation, so changing theme/photo always routes
  // back through an explicit "Générer".
  const STEPS = [ds("steps_theme"), ds("steps_photo"), ds("steps_email"), ds("steps_result")];
  // Fine-line pictograms, one per step of the atelier: ambiance, shot, note, frame.
  const icon = (paths) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
  const ICONS = [
    icon('<path d="M12 3l1.9 5.2a2 2 0 0 0 1.2 1.2L20.3 11l-5.2 1.9a2 2 0 0 0-1.2 1.2L12 19.3l-1.9-5.2a2 2 0 0 0-1.2-1.2L3.7 11l5.2-1.9a2 2 0 0 0 1.2-1.2L12 3z"/>'),
    icon('<path d="M14.5 4h-5L7.7 6.2H5a2 2 0 0 0-2 2V18a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.2a2 2 0 0 0-2-2h-2.7L14.5 4z"/><circle cx="12" cy="13" r="3.2"/>'),
    icon('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7.5 9 5.8 9-5.8"/>'),
    icon('<rect x="3.5" y="3.5" width="17" height="17" rx="2"/><circle cx="9" cy="9" r="1.8"/><path d="M20.5 15.5 17 12a1.8 1.8 0 0 0-2.5 0L6 20.5"/>'),
  ];
  const CHECK = icon('<path d="M5 12.5l4.5 4.5L19 7.5"/>');
  const RENDERERS = () => [renderTheme, renderPhoto, renderEmail, renderResult];
  function canGoTo(i) {
    if (i === 0) return true;
    if (i === 1) return !!S.themeKey;
    if (i === 2) return !!S.uploadId;
    if (i === 3) return genFresh();
    return false;
  }
  function stepDone(i) {
    if (i === 0) return !!S.themeKey;
    if (i === 1) return !!S.uploadId;
    if (i === 2) return genFresh();
    return false;
  }
  function goTo(i) { if (canGoTo(i)) RENDERERS()[i](); }
  function stepper(current) {
    return `<div class="pw-steps">${STEPS.map((label, i) => {
      const clickable = canGoTo(i) && i !== current;
      const cls = `pw-st${stepDone(i) && i !== current ? " is-done" : ""}${i === current ? " is-on" : ""}${clickable ? " is-clickable" : ""}`;
      const tag = clickable ? "button" : "div";
      const attr = clickable ? ` type="button" data-goto="${i}"` : "";
      return `<${tag} class="${cls}"${attr}><span class="pw-st-num">${stepDone(i) && i !== current ? CHECK : ICONS[i]}</span><span class="pw-st-label">${label}</span></${tag}>`;
    }).join("")}</div>`;
  }
  function wire() {
    screen.querySelectorAll("[data-goto]").forEach((b) => b.addEventListener("click", () => goTo(+b.dataset.goto)));
  }

  // Renders a screen. `key` names it: re-rendering the SAME key (chip clicks,
  // view toggle, inline errors) adds .pw-still so the entrance animation only
  // plays on real screen changes.
  let lastScreen = null;
  function el(html, key) {
    screen.classList.toggle("pw-still", key === lastScreen);
    lastScreen = key;
    screen.innerHTML = html;
  }

  async function api(path, opts) {
    const res = await fetch(cfg.appBaseUrl + path, opts);
    let body = null; try { body = await res.json(); } catch (_) {}
    return { status: res.status, body };
  }

  // --- Steps -----------------------------------------------------------------
  function renderTheme() {
    S.step = 0;
    el(`${stepper(0)}<h2 class="pw-title">${ds("theme_title")}</h2>
      <div class="pw-grid">${cfg.themes.map((th) => `
        <button class="pw-theme ${S.themeKey === th.key ? "is-sel" : ""}" data-key="${th.key}" data-label="${th.label}">
          <img src="${th.thumb}" alt="">
          <span class="pw-theme-overlay"><span class="pw-theme-name">${th.label}</span>${th.hint ? `<span class="pw-theme-hint">${th.hint}</span>` : ""}</span>
          <span class="pw-theme-badge">${CHECK}</span>
        </button>`).join("")}</div>`, "theme");
    wire();
    screen.querySelectorAll(".pw-theme").forEach((b) => b.addEventListener("click", () => {
      S.themeKey = b.dataset.key; S.themeLabel = b.dataset.label; renderPhoto();
    }));
  }

  function renderPhoto() {
    S.step = 1;
    el(`${stepper(1)}<h2 class="pw-title">${ds("photo_title")}</h2>
      ${twoCol(
        `<label class="pw-drop"><span class="pw-drop-hint">${ds("photo_hint")}</span><input type="file" accept="image/jpeg,image/png,image/webp" hidden></label>`,
        `<div class="pw-chosen"><img src="${themeThumb()}" alt=""><span>${ds("chosen_theme")} · <strong>${S.themeLabel}</strong></span><button type="button" class="pw-change" data-goto="0">${ds("chosen_change")}</button></div>
         <p class="pw-aside-lead">${ds("tips_title")}</p>
         <ul class="pw-tips"><li>${ds("tips_1")}</li><li>${ds("tips_2")}</li><li>${ds("tips_3")}</li></ul>
         <div class="pw-err" hidden></div>
         <button class="pw-btn" data-next ${S.file ? "" : "disabled"}>${ds("photo_cta")}</button>
         <button class="pw-btn pw-btn--ghost" data-back>${ds("back")}</button>`
      )}`, "photo");
    wire();
    const input = screen.querySelector("input[type=file]");
    const drop = screen.querySelector(".pw-drop");
    const next = screen.querySelector("[data-next]");
    const err = screen.querySelector(".pw-err");
    function showPhoto(f) { drop.style.backgroundImage = `url(${URL.createObjectURL(f)})`; drop.classList.add("pw-drop--filled"); }
    if (S.file) showPhoto(S.file);
    input.addEventListener("change", () => {
      const f = input.files[0]; if (!f) return;
      if (!["image/jpeg","image/png","image/webp"].includes(f.type)) { err.textContent = ds("err_upload_type"); err.hidden = false; return; }
      err.hidden = true; S.file = f; next.disabled = false; showPhoto(f);
      if (f !== S.uploadedFile) S.uploadId = null; // a new photo invalidates the previous upload + generation
    });
    next.addEventListener("click", continueFromPhoto);
    screen.querySelector("[data-back]").addEventListener("click", renderTheme);
  }

  async function continueFromPhoto() {
    if (S.uploadId && S.file === S.uploadedFile) return renderEmail(); // same photo already uploaded
    setLoading();
    const fd = new FormData(); fd.append("photo", S.file);
    const { status, body } = await api("/api/upload", { method: "POST", body: fd });
    if (status === 422) return renderPhotoError(ds("err_moderation"));
    if (status !== 200 || !body || !body.uploadId) return renderPhotoError(ds("err_generic"));
    S.uploadId = body.uploadId; S.uploadedFile = S.file; renderEmail();
  }
  function renderPhotoError(msg) {
    renderPhoto();
    const err = screen.querySelector(".pw-err"); if (err) { err.textContent = msg; err.hidden = false; }
  }

  function renderEmail() {
    S.step = 2;
    const fresh = genFresh();
    el(`${stepper(2)}<h2 class="pw-title">${ds("email_title")}</h2>
      ${twoCol(
        `<div class="pw-promo"><img src="${themeThumb()}" alt=""><span class="pw-promo-cap">${ds("promo_style")} ${S.themeLabel}</span></div>`,
        `<p class="pw-hint">${ds("email_hint")}</p>
         <input class="pw-input" type="email" placeholder="prenom@email.com" value="${S.email}">
         <label class="pw-consent"><input type="checkbox" ${S.consent ? "checked" : ""}><span>${ds("email_consent")}</span></label>
         <div class="pw-err" hidden></div>
         <button class="pw-btn" data-next ${fresh ? "" : "disabled"}>${fresh ? ds("see_portrait") : ds("email_cta")}</button>
         <button class="pw-btn pw-btn--ghost" data-back>${ds("back")}</button>`
      )}`, "email");
    wire();
    const email = screen.querySelector("input[type=email]");
    const consent = screen.querySelector("input[type=checkbox]");
    const next = screen.querySelector("[data-next]");
    const sync = () => { S.email = email.value.trim(); S.consent = consent.checked; if (!genFresh()) next.disabled = !(/.+@.+\..+/.test(S.email) && S.consent); };
    email.addEventListener("input", sync); consent.addEventListener("change", sync);
    if (!fresh) sync();
    next.addEventListener("click", () => { if (genFresh()) renderResult(); else generate(); });
    screen.querySelector("[data-back]").addEventListener("click", renderPhoto);
  }
  function renderEmailError(msg) {
    renderEmail();
    const err = screen.querySelector(".pw-err"); if (err) { err.textContent = msg; err.hidden = false; }
  }

  async function generate() {
    setPaintingLoader();
    const { status, body } = await api("/api/generate", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uploadId: S.uploadId, themeKey: S.themeKey, email: S.email, sessionId }),
    });
    if (status === 429) return renderEmailError(ds("err_quota"));
    if (status === 422) return renderPhotoError(ds("err_moderation"));
    if (status !== 200 || !body || !body.designId) return renderEmailError(ds("err_generic"));
    S.designId = body.designId; S.previewUrl = body.previewUrl;
    S.genThemeKey = S.themeKey; S.genUploadId = S.uploadId; // remember what this generation was made from
    if (typeof body.remaining === "number") S.remaining = body.remaining;
    ownsDesign = true;
    saveSnapshot();
    setDesignParam(S.designId);
    renderResult(true); // URL is fresh from this generation, no need to refetch
  }

  // Final step: big mockup (with view toggle) + size/frame options + price +
  // add-to-cart. The saved generation is shown as-is on every visit; an explicit
  // "autre version" link is the only intentional way to re-generate here.
  function renderResult(skipRefresh) {
    S.step = 3;
    if (!S.format) S.format = formats[0];
    if (!S.finition) S.finition = finitions[0];
    if (skipRefresh || !S.designId) return drawResult();
    // The preview URL is a presigned R2 link that expires; refresh it on every
    // visit (GET /api/design/:id) so a portrait generated earlier still shows.
    setLoading();
    api(`/api/design/${S.designId}`, {})
      .then(({ status, body }) => { if (status === 200 && body && body.previewUrl) S.previewUrl = body.previewUrl; })
      .catch(() => {})
      .then(drawResult);
  }
  function drawResult() {
    const hasRoom = !!cfg.roomImage;
    const draw = () => {
      const v = pickVariant(cfg.variants, S.format, S.finition);
      const toggle = hasRoom ? `<div class="pw-toggle">
          <button type="button" data-view="room" class="${S.view === "room" ? "is-on" : ""}">${ds("view_room")}</button>
          <button type="button" data-view="art" class="${S.view === "art" ? "is-on" : ""}">${ds("view_art")}</button>
        </div>` : "";
      el(`${stepper(3)}<h2 class="pw-title">${ds("preview_title")}</h2>
        ${twoCol(`${toggle}${mockup()}`, `
          <div><p class="pw-eyebrow">${ds("result_eyebrow")}</p><div class="pw-result-meta">${S.themeLabel}</div></div>
          <div class="pw-label">${ds("options_format")}</div>
          <div class="pw-row" data-group="format">${formats.map((f) => `<button type="button" class="pw-chip ${f === S.format ? "is-sel" : ""}" data-val="${f}">${f}</button>`).join("")}</div>
          <div class="pw-label">${ds("options_finition")}</div>
          <div class="pw-row" data-group="finition">${finitions.map((f) => `<button type="button" class="pw-chip ${f === S.finition ? "is-sel" : ""}" data-val="${f}">${f}</button>`).join("")}</div>
          <div class="pw-price">${v ? money(v.price) : "—"}</div>
          <div class="pw-err" hidden></div>
          <button class="pw-btn" data-add ${v ? "" : "disabled"}>${ds("recap_cta")}</button>
          <button class="pw-btn pw-btn--ghost" data-back>${ds("back")}</button>
          <button type="button" class="pw-restart">${ds("restart")}</button>
          <p class="pw-keep">${ds("result_keep")}</p>`)}`, "result");
      wire();
      screen.querySelectorAll("[data-view]").forEach((b) => b.addEventListener("click", () => { S.view = b.dataset.view; draw(); }));
      screen.querySelectorAll("[data-group=format] .pw-chip").forEach((c) => c.addEventListener("click", () => { S.format = c.dataset.val; saveSnapshot(); draw(); }));
      screen.querySelectorAll("[data-group=finition] .pw-chip").forEach((c) => c.addEventListener("click", () => { S.finition = c.dataset.val; saveSnapshot(); draw(); }));
      screen.querySelector("[data-add]").addEventListener("click", (e) => { if (v) addToCart(v, e.currentTarget); });
      screen.querySelector("[data-back]").addEventListener("click", renderEmail);
      screen.querySelector(".pw-restart").addEventListener("click", restart);
    };
    draw();
  }
  function renderResultError(msg) {
    renderResult(true);
    const err = screen.querySelector(".pw-err"); if (err) { err.textContent = msg; err.hidden = false; }
  }

  async function addToCart(v, btn) {
    // Same mechanism as the theme's product form: keep the screen in place,
    // load only the button, ask Shopify to render the cart sections in the
    // same response and hand them to the theme's cart popup/drawer.
    const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    if (btn) { btn.disabled = true; btn.classList.add("is-busy"); }
    const res = await fetch("/cart/add.js", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: v.id, quantity: 1,
        ...(cart ? { sections: cart.getSectionsToRender().map((s) => s.id), sections_url: window.location.pathname } : {}),
        properties: {
          _design_id: S.designId, _preview_url: S.previewUrl, _theme: S.themeKey,
          "Thème": S.themeLabel,
        },
      }),
    });
    if (btn) { btn.disabled = false; btn.classList.remove("is-busy"); }
    if (!res.ok) return renderResultError(ds("err_generic"));
    const body = await res.json().catch(() => null);
    if (cart && body && body.sections) {
      cart.setActiveElement(document.activeElement);
      cart.renderContents(body);
    } else {
      window.location.href = "/cart";
    }
  }

  // Quick waits (upload, preview refresh, cart): a discreet spinner.
  let loaderTimer = null;
  function clearLoaderTimer() {
    if (loaderTimer) { clearInterval(loaderTimer); loaderTimer = null; }
  }
  function setLoading() {
    clearLoaderTimer();
    el(`<div class="pw-wait">${ds("loading_wait")}</div>`, "wait");
  }

  // Generation loader (~50s in production): the customer's own photo comes to
  // life inside the gold frame (CSS reveal) while the real generation stages
  // tick off, paced on the actual duration. The last stage stays active until
  // the backend answers — the finished portrait is the real reveal.
  const STAGE_AT = [0, 14, 30, 44]; // seconds at which each stage starts
  function setPaintingLoader() {
    clearLoaderTimer();
    const stages = [ds("loading_1"), ds("loading_2"), ds("loading_3"), ds("loading_4")];
    const photo = S.file ? URL.createObjectURL(S.file) : themeThumb();
    el(`<div class="pw-loader">
      <div class="pw-paint-frame"><img class="pw-paint-photo" src="${photo}" alt=""></div>
      <div class="pw-load-bar"><i></i></div>
      <ul class="pw-load-steps">${stages.map((s) => `<li>${s}</li>`).join("")}</ul>
      <p class="pw-load-eta">${ds("loading_eta")}</p>
    </div>`, "painting");
    const items = Array.from(screen.querySelectorAll(".pw-load-steps li"));
    let sec = 0;
    const paint = () => items.forEach((li, i) => {
      const started = sec >= STAGE_AT[i];
      const finished = i + 1 < STAGE_AT.length && sec >= STAGE_AT[i + 1];
      li.classList.toggle("is-done", finished);
      li.classList.toggle("is-on", started && !finished);
    });
    paint();
    loaderTimer = setInterval(() => {
      if (!items[0] || !items[0].isConnected) { clearLoaderTimer(); return; }
      sec += 1; paint();
    }, 1000);
  }

  // strings: read translated UI labels from the apostrophe-safe config island.
  function ds(key) { const m = cfg.strings || {}; return m[key] || key; }

  // "Créer un nouveau portrait": wipe the saved generation and start over.
  function restart() {
    clearSnapshot();
    ownsDesign = false;
    setDesignParam(null);
    S.themeKey = null; S.themeLabel = null;
    S.uploadId = null; S.uploadedFile = null;
    S.designId = null; S.previewUrl = null; S.remaining = cfg.maxRegen;
    S.genThemeKey = null; S.genUploadId = null;
    S.file = null; S.email = ""; S.consent = false;
    S.format = null; S.finition = null; S.view = "room";
    renderTheme();
  }

  function applyDesign(body, snap) {
    S.designId = body.designId;
    S.previewUrl = body.previewUrl;
    S.themeKey = body.theme; S.genThemeKey = body.theme;
    const t = cfg.themes.find((x) => x.key === body.theme);
    S.themeLabel = (snap && snap.themeLabel) || (t ? t.label : body.theme);
    if (snap) {
      // Same-device resume: full context (regen via the photo step, quota, options).
      S.uploadId = snap.uploadId || null; S.genUploadId = snap.uploadId || null;
      S.email = snap.email || "";
      if (typeof snap.remaining === "number") S.remaining = snap.remaining;
      if (snap.format) S.format = snap.format;
      if (snap.finition) S.finition = snap.finition;
    }
  }

  // Boot: a ?design=<uuid> link wins over the local snapshot; the backend
  // decides whether the design is still resumable (preview + not purged).
  async function boot(retried) {
    let designId = null;
    try { designId = new URL(window.location.href).searchParams.get("design"); } catch (_) {}
    let snap = readSnapshot();
    if (designId) {
      if (snap && snap.designId !== designId) snap = null; // foreign link: no local context
    } else {
      designId = snap && snap.designId;
    }
    if (!designId) return renderTheme();

    setLoading();
    let res = null;
    try { res = await api(`/api/design/${designId}`, {}); } catch (_) {}
    if (res && res.status === 200 && res.body && res.body.previewUrl && res.body.status === "preview") {
      applyDesign(res.body, snap);
      ownsDesign = !!snap; // same-device resume owns it; a shared link stays view-only
      saveSnapshot();
      setDesignParam(S.designId);
      renderResult(true); // previewUrl just fetched, skip the refresh round-trip
    } else if (res && (res.status === 404 || (res.body && res.body.status && res.body.status !== "preview"))) {
      // Purged or already ordered: this design is gone for good.
      if (snap && snap.designId === designId) clearSnapshot();
      setDesignParam(null);
      // A dead link falls back to the visitor's own snapshot; the retried
      // flag guarantees termination even if stripping the param failed.
      if (retried) renderTheme(); else boot(true);
    } else {
      // Network hiccup: keep the snapshot AND the ?design param so a plain
      // reload retries the resume; run the normal flow meanwhile.
      renderTheme();
    }
  }

  boot();
})();
